// Standard Report Center — frontend template registry (Increment R2 of the
// reporting foundation, docs/reporting-foundation.md). Every template is backed
// by a REAL endpoint that already exists and is role-gated server-side; this
// registry only decides visibility (canonical RBAC, ANY-of) and how to fetch +
// shape rows for the generic viewer. The server-side catalog arrives in R4 —
// nothing here calls a "registry" backend.

import {
  getAllEmployees,
  getReadinessSummary,
  getPendingApprovals,
  getOrgBalances,
  type Employee,
  type OrgEmployeeBalances,
} from '@/lib/hrApi';
import { getPayrollReadiness } from '@/services/payrollSettingsService';
import {
  listBatches,
  listExceptions,
  listOvertime,
  formatMinutes,
  type ExceptionStatus,
  type OvertimeStatus,
} from '@/services/attendanceService';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import { creditApi } from '@/features/credit/api/creditApi';
import { getPayrollSummaryReport } from '@/features/payroll/api/reports';
import { hasPermission } from '@/rbac/rbac.generated';
import type { ReportDomain } from '@/services/savedReportService';
import { previewReport, type ReportPreviewBody } from '@/services/reportEngineService';

// ---- Types -------------------------------------------------------------------

export type TemplateKind = 'live' | 'snapshot' | 'passthrough';

export type ReportCategory = 'owner' | 'hr' | 'finance' | 'attendance' | 'risk' | 'audit';

export interface ReportColumn {
  key: string;
  label: string;
}

export interface ReportResult {
  columns: ReportColumn[];
  rows: Record<string, any>[];
}

export interface ReportFetchCtx {
  runId?: string;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  status?: string;
}

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: ReportCategory;
  /** Also list the same template under these extra categories (single source of truth). */
  alsoIn?: ReportCategory[];
  /** Server-side report domain (saved definitions API, R3). */
  domain: ReportDomain;
  kind: TemplateKind;
  /** ANY-of, canonical permission keys from rbac.generated. */
  requiredPermissions: string[];
  /** Shows a confidentiality badge on the card + banner/CSV row in the viewer. */
  sensitive?: boolean;
  /** Payroll snapshot/passthrough templates that need a run selected. */
  needsRunPicker?: boolean;
  /** Extra gate: hidden from users whose ONLY tenant role is `employee`. */
  managerOnly?: boolean;
  /** Passthrough navigation target (relative to the tenant root). */
  route?: string;
  fetch?: (ctx: ReportFetchCtx) => Promise<ReportResult>;
  /** Which filter controls the viewer shows. */
  filters?: ('dateRange' | 'status')[];
  statusOptions?: string[];
  /** Default look-back (negative) / look-ahead (positive) window in days for dateRange. */
  defaultRangeDays?: number;
  /** Report-engine preset behind `fetch` — lets the viewer offer the SERVER
   *  export (CSV/XLSX, full rows, same scope) for curated engine templates. */
  preset?: EnginePreset;
}

export type EnginePreset = Omit<ReportPreviewBody, 'filters'> & { filters?: Record<string, string> };

// Performance cycle outcomes (rewards Phase 5): flat per-participant rows from
// performance-service via the gateway; requires performance:analytics there.
async function fetchPerformanceRows(): Promise<ReportResult> {
  const { gatewayApi } = await import('@/lib/hrApi');
  const rows = (await gatewayApi.get('/api/v1/performance/reports/rows')).data as any[];
  return {
    columns: [
      { key: 'cycle_name', label: 'Cycle' },
      { key: 'employee_name', label: 'Employee' },
      { key: 'department', label: 'Department' },
      { key: 'status', label: 'Review status' },
      { key: 'self_review_status', label: 'Self review' },
      { key: 'manager_review_status', label: 'Manager review' },
      { key: 'final_rating', label: 'Final rating' },
      { key: 'final_score', label: 'Final score' },
      { key: 'released', label: 'Released' },
    ],
    rows: rows || [],
  };
}

// Training compliance (L&D expansion L7): flat per-assignment rows.
async function fetchLearningRows(): Promise<ReportResult> {
  const { gatewayApi } = await import('@/lib/hrApi');
  const rows = (await gatewayApi.get('/api/v1/learning/reports/rows')).data as any[];
  return {
    columns: [
      { key: 'employee_name', label: 'Employee' },
      { key: 'course', label: 'Course' },
      { key: 'status', label: 'Status' },
      { key: 'source', label: 'Source' },
      { key: 'due_date', label: 'Due' },
      { key: 'completed_at', label: 'Completed' },
      { key: 'verified', label: 'Verified' },
      // L&D Phase 5: attendance / track / practice / policy / readiness per row (server-computed; '' when not applicable).
      { key: 'attendance', label: 'Attendance' },
      { key: 'track', label: 'Track' },
      { key: 'practice_status', label: 'Practice' },
      { key: 'policy_satisfied', label: 'Policy satisfied' },
      { key: 'readiness', label: 'Readiness' },
    ],
    rows: (rows || []).map((r) => ({ ...r, verified: r.verified ? 'yes' : 'no' })),
  };
}

// ---- Small helpers -----------------------------------------------------------

const fullName = (e: Employee) =>
  `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email || e.employee_id;

const inRange = (d: string | null | undefined, from?: string, to?: string): boolean => {
  if (!d) return false;
  const day = d.slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

const col = (key: string, label: string): ReportColumn => ({ key, label });

// runPreset executes a preset report-engine query (a curated card that pins a
// domain + columns/group_by/aggregations) and maps the engine result to the
// generic viewer's {columns, rows}. The date-range/status filters the viewer
// passes are merged over the preset's own filters. Used by the Work Activity
// Reporting (Phase B2) curated views, which are grouped queries on work_activity.
const runPreset =
  (preset: Omit<ReportPreviewBody, 'filters'> & { filters?: Record<string, string> }) =>
  async ({ from, to, status }: ReportFetchCtx): Promise<ReportResult> => {
    const filters: Record<string, string> = { ...(preset.filters ?? {}) };
    if (from) filters.from = from;
    if (to) filters.to = to;
    if (status) filters.status = status;
    const res = await previewReport({ ...preset, filters });
    return {
      columns: (res.columns ?? []).map((c) => ({ key: c.key, label: c.label })),
      rows: res.rows as Record<string, any>[],
    };
  };

// enginePreset: a curated engine template — the same preset drives the preview
// (fetch) and the server export (preset), so screen and file cannot disagree.
const enginePreset = (preset: EnginePreset) => ({ preset, fetch: runPreset(preset) });

// ---- Attendance standard templates (P4) ------------------------------------------
//
// All nine read the report engine's `attendance` domain, which delegates to
// leave-service /attendance/days with the VIEWER's token: a manager's report
// is exactly their team, as on the Workforce Overview. Narrowing (late,
// missing, overtime, corrections) happens server-side via the `flag` filter.
const ATT_DAY = ['employee_name', 'department', 'branch', 'work_date', 'first_punch_at', 'last_punch_at',
  'gross_minutes', 'break_minutes', 'net_minutes', 'source', 'day_status', 'exception_count'];

export const ATTENDANCE_TEMPLATE_IDS = [
  'attendance-daily-register', 'attendance-period-summary', 'attendance-employee-timesheet',
  'attendance-late-early', 'attendance-missing-exceptions', 'attendance-overtime-hours',
  'attendance-dept-branch', 'attendance-source-summary', 'attendance-corrections-audit',
] as const;

const attendanceTemplate = (
  id: (typeof ATTENDANCE_TEMPLATE_IDS)[number], name: string, description: string,
  preset: EnginePreset, defaultRangeDays = -30, requiredPermissions = ['attendance:read'],
): ReportTemplate => ({
  id, name, description, category: 'attendance', domain: 'attendance', kind: 'live',
  requiredPermissions, filters: ['dateRange'], defaultRangeDays, ...enginePreset(preset),
});

const ATTENDANCE_STANDARD: ReportTemplate[] = [
  attendanceTemplate('attendance-daily-register', 'Daily attendance register',
    'Every employee-day: first in, last out, gross, break, worked, source and status.',
    { domain: 'attendance', columns: ATT_DAY }, -1),
  attendanceTemplate('attendance-period-summary', 'Weekly / monthly attendance summary',
    'One row per employee for the period: days, worked, regular, overtime and lateness.',
    { domain: 'attendance', columns: ['employee_name'], group_by: ['employee_name', 'department'],
      aggregations: ['count', 'sum:net_minutes', 'sum:regular_minutes', 'sum:potential_ot_minutes',
        'sum:approved_ot_minutes', 'sum:late_minutes'] }),
  attendanceTemplate('attendance-employee-timesheet', 'Employee timesheet',
    'Day-by-day hours for each employee, with the breakdown behind every figure.',
    { domain: 'attendance', columns: ['employee_name', 'work_date', 'first_punch_at', 'last_punch_at', 'gross_minutes',
      'break_minutes', 'time_away_minutes', 'net_minutes', 'regular_minutes', 'potential_ot_minutes',
      'approved_ot_minutes', 'status'] }, -14),
  attendanceTemplate('attendance-late-early', 'Late arrivals & early departures',
    'Days that started after the grace period or ended before the scheduled end.',
    { domain: 'attendance', columns: ['employee_name', 'department', 'work_date', 'first_punch_at', 'last_punch_at',
      'late_minutes', 'early_departure_minutes'], filters: { flag: 'late_early' } }),
  attendanceTemplate('attendance-missing-exceptions', 'Missing punches & exceptions',
    'Single punches, days with no worked time, and days with open review items.',
    { domain: 'attendance', columns: ['employee_name', 'department', 'work_date', 'first_punch_at', 'punch_count',
      'net_minutes', 'day_status', 'exception_count'], filters: { flag: 'missing_or_exceptions' } }),
  attendanceTemplate('attendance-overtime-hours', 'Overtime & worked hours',
    'Worked, regular, potential and approved overtime per employee. Only approved overtime is paid.',
    { domain: 'attendance', columns: ['employee_name'], group_by: ['employee_name', 'department'],
      aggregations: ['sum:net_minutes', 'sum:regular_minutes', 'sum:potential_ot_minutes', 'sum:approved_ot_minutes'] }),
  attendanceTemplate('attendance-dept-branch', 'Department & branch attendance',
    'Days, worked time, lateness and open exceptions per department and branch.',
    { domain: 'attendance', columns: ['department'], group_by: ['department', 'branch'],
      aggregations: ['count', 'sum:net_minutes', 'sum:late_minutes', 'sum:exception_count'] }),
  attendanceTemplate('attendance-source-summary', 'Biometric vs self check-in',
    'How each day was captured — scanner, self check-in, merged, file or manual — with worked time.',
    { domain: 'attendance', columns: ['source'], group_by: ['source'], aggregations: ['count', 'sum:net_minutes'] }),
  attendanceTemplate('attendance-corrections-audit', 'Attendance corrections & audit history',
    'Hand-corrected days, correction requests on locked days, and days changed by a late sync.',
    { domain: 'attendance', columns: ['employee_name', 'work_date', 'source', 'status', 'net_minutes',
      'correction_reason', 'exception_count'], filters: { flag: 'corrections' } },
    -30, ['attendance:correct']),
];

// ---- Fetchers ------------------------------------------------------------------

async function fetchPayrollSummary({ runId }: ReportFetchCtx): Promise<ReportResult> {
  if (!runId) return { columns: [], rows: [] };
  const rep = await getPayrollSummaryReport(runId);
  const columns = [
    col('item', 'Item'),
    col('employee_count', 'Employees'),
    col('gross', 'Gross'),
    col('net', 'Net'),
    col('amount', 'Amount'),
  ];
  const headline: [string, string | number][] = [
    ['Period', rep.period],
    ['Currency', rep.currency],
    ['Employees', rep.employee_count],
    ['Gross', rep.gross],
    ['Taxable income', rep.taxable_income],
    ['Total deductions', rep.total_deductions],
    ['Net', rep.net],
    ['Employer cost', rep.employer_cost],
    ['PAYE', rep.paye],
    ['NSSF (employee)', rep.nssf_employee],
    ['NSSF (employer)', rep.nssf_employer],
    ['SDL', rep.sdl],
    ['WCF', rep.wcf],
    ['HESLB', rep.heslb],
  ];
  const rows: Record<string, any>[] = headline.map(([item, amount]) => ({ item, amount }));
  for (const d of rep.departments ?? []) {
    rows.push({ item: `Department: ${d.key}`, employee_count: d.employee_count, gross: d.gross, net: d.net });
  }
  for (const c of rep.cost_centers ?? []) {
    rows.push({ item: `Cost center: ${c.key}`, employee_count: c.employee_count, gross: c.gross, net: c.net });
  }
  return { columns, rows };
}

async function fetchPayrollRiskSummary({ runId }: ReportFetchCtx): Promise<ReportResult> {
  if (!runId) return { columns: [], rows: [] };
  const cc = await payrollApi.getControlCenter(runId);
  const columns = [
    col('severity', 'Severity'),
    col('category', 'Category'),
    col('message', 'Message'),
    col('status', 'Status'),
  ];
  const rows: Record<string, any>[] = [
    {
      severity: `risk score ${cc.riskScore}/100`,
      category: 'summary',
      message: `Gate: ${cc.gate.status}${cc.gate.blocking.length ? ` — blocking: ${cc.gate.blocking.join(', ')}` : ''}`,
      status: `${cc.alerts.length} alert(s)`,
    },
    ...cc.alerts.map((a) => ({
      severity: a.severity,
      category: a.category,
      message: a.message,
      status: a.status,
    })),
  ];
  return { columns, rows };
}

async function fetchNewHires({ from, to }: ReportFetchCtx): Promise<ReportResult> {
  const employees = await getAllEmployees();
  const rows = employees
    .filter((e) => inRange(e.hire_date, from, to))
    .sort((a, b) => (b.hire_date ?? '').localeCompare(a.hire_date ?? ''))
    .map((e) => ({
      name: fullName(e),
      employee_code: e.employee_id,
      position: e.position ?? '',
      department: e.department_id ?? '',
      hire_date: e.hire_date ?? '',
      status: e.status ?? '',
    }));
  return {
    columns: [
      col('name', 'Name'),
      col('employee_code', 'Employee code'),
      col('position', 'Position'),
      col('department', 'Department'),
      col('hire_date', 'Hire date'),
      col('status', 'Status'),
    ],
    rows,
  };
}

async function fetchEmployeeMasterList(): Promise<ReportResult> {
  const employees = await getAllEmployees();
  return {
    columns: [
      col('employee_id', 'Employee ID'),
      col('name', 'Name'),
      col('email', 'Email'),
      col('position', 'Position'),
      col('employment_type', 'Employment type'),
      col('status', 'Status'),
      col('hire_date', 'Hire date'),
      col('phone', 'Phone'),
    ],
    rows: employees.map((e) => ({
      employee_id: e.employee_id,
      name: fullName(e),
      email: e.email,
      position: e.position ?? '',
      employment_type: e.employment_type ?? '',
      status: e.status ?? '',
      hire_date: e.hire_date ?? '',
      phone: e.phone ?? '',
    })),
  };
}

async function fetchDataCompleteness(): Promise<ReportResult> {
  const s = await getReadinessSummary();
  const columns = [
    col('name', 'Metric / Employee'),
    col('value', 'Value'),
    col('score', 'Score'),
    col('missing_count', 'Missing fields'),
    col('duplicates', 'Duplicates'),
  ];
  const countRows: [string, number | string][] = [
    ['Total employees', s.counts.total],
    ['Complete profiles', s.counts.complete],
    ['Average score', s.average_score],
    ['Missing TIN', s.counts.missing_tin],
    ['Missing NSSF', s.counts.missing_nssf],
    ['Missing national ID', s.counts.missing_national_id],
    ['Missing scanner ID', s.counts.missing_scanner_id],
    ['Missing phone', s.counts.missing_phone],
    ['Missing employment type', s.counts.missing_employment_type],
    ['With duplicate values', s.counts.with_duplicates],
  ];
  const rows: Record<string, any>[] = countRows.map(([name, value]) => ({ name, value }));
  for (const e of s.least_complete ?? []) {
    rows.push({
      name: e.name,
      score: e.score,
      missing_count: e.missing_count,
      duplicates: (e.duplicates ?? []).join('; '),
    });
  }
  return { columns, rows };
}

function makeEmployeeDateWindowFetch(
  field: 'contract_end_date' | 'probation_end_date',
  extraColumns: ReportColumn[],
  toRow: (e: Employee) => Record<string, any>,
) {
  return async ({ from, to }: ReportFetchCtx): Promise<ReportResult> => {
    const employees = await getAllEmployees();
    const rows = employees
      .filter((e) => inRange(e[field] as string | null | undefined, from, to))
      .sort((a, b) => String(a[field] ?? '').localeCompare(String(b[field] ?? '')))
      .map(toRow);
    return { columns: extraColumns, rows };
  };
}

const fetchExpiringContracts = makeEmployeeDateWindowFetch(
  'contract_end_date',
  [
    col('name', 'Name'),
    col('employment_type', 'Employment type'),
    col('contract_start_date', 'Contract start'),
    col('contract_end_date', 'Contract end'),
    col('status', 'Status'),
  ],
  (e) => ({
    name: fullName(e),
    employment_type: e.employment_type ?? '',
    contract_start_date: e.contract_start_date ?? '',
    contract_end_date: e.contract_end_date ?? '',
    status: e.status ?? '',
  }),
);

const fetchProbationEnding = makeEmployeeDateWindowFetch(
  'probation_end_date',
  [
    col('name', 'Name'),
    col('employment_type', 'Employment type'),
    col('hire_date', 'Hire date'),
    col('probation_end_date', 'Probation ends'),
    col('status', 'Status'),
  ],
  (e) => ({
    name: fullName(e),
    employment_type: e.employment_type ?? '',
    hire_date: e.hire_date ?? '',
    probation_end_date: e.probation_end_date ?? '',
    status: e.status ?? '',
  }),
);

async function fetchPendingLeave(): Promise<ReportResult> {
  // /leave-requests/pending returns employee_id = auth user id; resolve names
  // from the directory (same decoupling the leave-service documents).
  const [pending, employees] = await Promise.all([
    getPendingApprovals().catch(() => [] as any[]),
    getAllEmployees().catch(() => [] as Employee[]),
  ]);
  const byUserId = new Map(employees.filter((e) => e.user_id).map((e) => [String(e.user_id), e]));
  return {
    columns: [
      col('employee', 'Employee'),
      col('type', 'Leave type'),
      col('start_date', 'Start'),
      col('end_date', 'End'),
      col('days', 'Days'),
      col('reason', 'Reason'),
      col('submitted_at', 'Submitted'),
    ],
    rows: (Array.isArray(pending) ? pending : []).map((r: any) => {
      const emp = byUserId.get(String(r.employee_id));
      return {
        employee: emp ? fullName(emp) : String(r.employee_id ?? ''),
        type: r.type ?? '',
        start_date: r.start_date ?? '',
        end_date: r.end_date ?? '',
        days: r.days ?? '',
        reason: r.reason ?? '',
        submitted_at: r.submitted_at ? String(r.submitted_at).slice(0, 10) : '',
      };
    }),
  };
}

async function fetchTeamLeaveBalances(): Promise<ReportResult> {
  const employees = await getAllEmployees();
  const withUsers = employees.filter((e) => e.user_id);
  const userIds = withUsers.map((e) => String(e.user_id));
  const balances = userIds.length
    ? await getOrgBalances(userIds).catch(() => [] as OrgEmployeeBalances[])
    : [];
  const byUserId = new Map(balances.map((b) => [String(b.employee_id), b]));
  return {
    columns: [
      col('member', 'Member'),
      col('entitled', 'Entitled'),
      col('taken', 'Taken'),
      col('pending', 'Pending'),
      col('available', 'Available'),
    ],
    rows: withUsers.map((e) => {
      const b = byUserId.get(String(e.user_id));
      return {
        member: fullName(e),
        entitled: b?.total_entitled ?? '',
        taken: b?.total_taken ?? '',
        pending: b?.total_pending ?? '',
        available: b?.total_available ?? '',
      };
    }),
  };
}

const READINESS_COLUMNS = [
  col('full_name', 'Employee'),
  col('email', 'Email'),
  col('payroll_enabled', 'Payroll enabled'),
  col('status', 'Status'),
  col('issues', 'Issues'),
];

async function fetchPayrollReadinessRows(onlyStatus?: string): Promise<ReportResult> {
  const r = await getPayrollReadiness();
  const employees = onlyStatus ? r.employees.filter((e) => e.status === onlyStatus) : r.employees;
  const rows: Record<string, any>[] = [
    {
      full_name: 'Summary',
      issues: `total ${r.summary.total} · ready ${r.summary.ready} · warning ${r.summary.warning} · blocked ${r.summary.blocked} · excluded ${r.summary.excluded}`,
    },
    ...employees.map((e) => ({
      full_name: e.full_name,
      email: e.email,
      payroll_enabled: e.payroll_enabled ? 'yes' : 'no',
      status: e.status,
      issues: (e.issues ?? []).map((i) => i.message).join('; '),
    })),
  ];
  return { columns: READINESS_COLUMNS, rows };
}

async function fetchLoansDeductions(): Promise<ReportResult> {
  // Loans are now sourced from credit-service (facilities); employee names come
  // from the payroll directory since facilities carry only an employee ref.
  const [facilities, employees] = await Promise.all([
    creditApi.listFacilities(),
    payrollApi.listEmployees().catch(() => []),
  ]);
  const names = new Map(employees.map((e) => [e.id, e.name]));
  return {
    columns: [
      col('employee', 'Employee'),
      col('principal', 'Principal'),
      col('balance', 'Balance'),
      col('installment', 'Installment'),
      col('currency', 'Currency'),
      col('status', 'Status'),
    ],
    rows: facilities.map((f) => ({
      employee: names.get(f.employeeRef) || f.employeeRef.slice(0, 8),
      principal: f.principal,
      balance: f.outstandingBalance,
      installment: f.installment,
      currency: f.currency,
      status: f.status,
    })),
  };
}

async function fetchAttendanceImportSummary(): Promise<ReportResult> {
  const batches = await listBatches();
  return {
    columns: [
      col('file', 'File'),
      col('period', 'Period'),
      col('rows', 'Rows'),
      col('matched', 'Matched'),
      col('unmatched', 'Unmatched'),
      col('exceptions', 'Exceptions'),
      col('status', 'Status'),
    ],
    rows: batches.map((b) => ({
      file: b.file_name,
      period: `${b.period_start} → ${b.period_end}`,
      rows: b.total_rows,
      matched: b.matched_rows,
      unmatched: b.unmatched_rows,
      exceptions: b.exception_count,
      status: b.status,
    })),
  };
}

const EXCEPTION_COLUMNS = [
  col('type', 'Type'),
  col('severity', 'Severity'),
  col('message', 'Message'),
  col('date', 'Date'),
  col('status', 'Status'),
];

async function fetchExceptions(status?: string, type?: string): Promise<ReportResult> {
  const list = await listExceptions((status as ExceptionStatus | 'all') || 'open');
  const filtered = type ? list.filter((x) => x.type === type) : list;
  return {
    columns: EXCEPTION_COLUMNS,
    rows: filtered.map((x) => ({
      type: x.type,
      severity: x.severity,
      message: x.message,
      date: x.work_date,
      status: x.status,
    })),
  };
}

const OVERTIME_COLUMNS = [
  col('employee', 'Employee'),
  col('date', 'Date'),
  col('minutes', 'Time'),
  col('multiplier', 'Multiplier'),
  col('status', 'Status'),
];

function makeOvertimeFetch(status: OvertimeStatus) {
  return async (): Promise<ReportResult> => {
    const list = await listOvertime(status);
    return {
      columns: OVERTIME_COLUMNS,
      rows: list.map((o) => ({
        employee: o.employee_id,
        date: o.work_date,
        minutes: formatMinutes(o.minutes_approved ?? o.minutes_requested),
        multiplier: `${o.multiplier}x`,
        status: o.status,
      })),
    };
  };
}

async function fetchDuplicateIdentity(): Promise<ReportResult> {
  const s = await getReadinessSummary();
  const rows: Record<string, any>[] = [
    { field: 'Employees with duplicate values', value: String(s.counts.with_duplicates) },
    ...Object.entries(s.duplicates ?? {}).map(([field, values]) => ({
      field,
      value: (values ?? []).join('; '),
    })),
  ];
  return { columns: [col('field', 'Field'), col('value', 'Duplicated value(s)')], rows };
}

// ---- Templates -----------------------------------------------------------------

const statutoryPassthrough = (
  id: string,
  name: string,
  description: string,
  domain: ReportDomain = 'statutory_compliance',
): ReportTemplate => ({
  id,
  name,
  description: `${description} Open a payroll run → Exports tab.`,
  category: 'finance',
  domain,
  kind: 'passthrough',
  requiredPermissions: ['payroll:read'],
  sensitive: true,
  needsRunPicker: true,
  route: 'payroll',
});

export const REPORT_TEMPLATES: ReportTemplate[] = [
  // ---- Owner ----
  {
    id: 'payroll-summary',
    name: 'Payroll summary',
    description: 'Headline totals, statutory amounts and department breakdown from a locked payroll run.',
    category: 'owner',
    domain: 'payroll',
    kind: 'snapshot',
    requiredPermissions: ['payroll:read'],
    sensitive: true,
    needsRunPicker: true,
    fetch: fetchPayrollSummary,
  },
  // ---- Work Activity Reporting (Phase B2 curated views) ----
  {
    id: 'work-today',
    name: "Who's working on what today",
    description: 'Work in flight today: who is actively on it, where, and whether it is overdue. Row-scoped to your team.',
    category: 'owner',
    alsoIn: ['hr'],
    domain: 'work_today',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    filters: ['status'],
    fetch: runPreset({
      domain: 'work_today',
      columns: ['person', 'department', 'role', 'source', 'title', 'status', 'priority', 'context_label', 'planned_end', 'overdue'],
    }),
  },
  {
    id: 'work-team-workload',
    name: 'Team & department workload',
    description: 'Active work volume and effort per department — one row per department, from assignments + work items.',
    category: 'owner',
    alsoIn: ['hr'],
    domain: 'work_activity',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    filters: ['dateRange', 'status'],
    fetch: runPreset({
      domain: 'work_activity',
      columns: ['department'],
      group_by: ['department'],
      aggregations: ['count', 'sum:effort_hours'],
    }),
  },
  {
    id: 'work-delivery',
    name: 'Executive work delivery',
    description: 'Delivery by status across all work — volume, effort and average turnaround. High-level cross-team view.',
    category: 'owner',
    alsoIn: ['hr'],
    domain: 'work_activity',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    filters: ['dateRange'],
    fetch: runPreset({
      domain: 'work_activity',
      columns: ['status'],
      group_by: ['status'],
      aggregations: ['count', 'sum:effort_hours', 'avg:turnaround_hours'],
    }),
  },
  {
    id: 'employees-no-work',
    name: 'Employees without active work',
    description: 'People in your scope with no work in flight today and not on approved leave — the idle / unassigned list.',
    category: 'owner',
    alsoIn: ['hr'],
    domain: 'employees_no_work',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    fetch: runPreset({
      domain: 'employees_no_work',
      columns: ['person', 'department', 'manager', 'position'],
    }),
  },
  {
    id: 'work-reconciliation',
    name: 'Assignment vs work reconciliation',
    description: 'Each assignment cross-checked against work items — which field work is (not) tracked as a work item, and the item status.',
    category: 'owner',
    alsoIn: ['hr'],
    domain: 'work_reconciliation',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    filters: ['status'],
    fetch: runPreset({
      domain: 'work_reconciliation',
      columns: ['person', 'department', 'assignment', 'assignment_status', 'context_label', 'tracked', 'work_item_status'],
    }),
  },
  {
    id: 'work-conflicts',
    name: 'Scheduling conflicts (double-booked)',
    description: 'Employees with overlapping assignments — each conflicting pair and the overlap window. Row-scoped to your team.',
    category: 'owner',
    alsoIn: ['hr', 'attendance'],
    domain: 'work_conflicts',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    fetch: runPreset({
      domain: 'work_conflicts',
      columns: ['person', 'department', 'assignment_a', 'a_start', 'a_end', 'assignment_b', 'b_start', 'b_end', 'overlap_start', 'overlap_end'],
    }),
  },
  {
    id: 'training-compliance',
    name: 'Training compliance',
    description: 'Training assignments, completion and verification per person (live).',
    category: 'hr',
    domain: 'learning',
    kind: 'live',
    requiredPermissions: ['learning:manage'],
    fetch: fetchLearningRows,
  },
  {
    id: 'performance-cycle-outcomes',
    name: 'Performance cycle outcomes',
    description: 'Per-participant review status, ratings and scores across cycles (live).',
    category: 'hr',
    // US-27: listed once in its most relevant hub (HR). Owners reach HR reports too.
    domain: 'performance',
    kind: 'live',
    requiredPermissions: ['performance:analytics'],
    sensitive: true,
    fetch: fetchPerformanceRows,
  },
  {
    id: 'payroll-risk-summary',
    name: 'Payroll risk summary',
    description: 'Pre-payroll Control Center: risk score, gate status and open fraud/compliance alerts for a run.',
    // US-27: listed once, in its most relevant hub (Risk & Fraud) rather than also under Owner.
    category: 'risk',
    domain: 'risk_fraud',
    kind: 'snapshot',
    requiredPermissions: ['payroll:approve'],
    sensitive: true,
    needsRunPicker: true,
    fetch: fetchPayrollRiskSummary,
  },
  {
    id: 'new-hires',
    name: 'New hires',
    description: 'Employees hired within the selected period.',
    category: 'owner',
    domain: 'employee',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    filters: ['dateRange'],
    defaultRangeDays: -90,
    fetch: fetchNewHires,
  },

  // ---- HR ----
  {
    id: 'employee-master-list',
    name: 'Employee master list',
    description: 'Full employee directory: code, contacts, position, employment type and status.',
    category: 'hr',
    domain: 'employee',
    kind: 'live',
    requiredPermissions: ['employee:read'],
    managerOnly: true,
    fetch: fetchEmployeeMasterList,
  },
  {
    id: 'data-completeness',
    name: 'Data completeness',
    description: 'Missing statutory IDs, phones and employment types, plus the least-complete profiles.',
    category: 'hr',
    domain: 'employee',
    kind: 'live',
    requiredPermissions: ['employee:write'],
    fetch: fetchDataCompleteness,
  },
  {
    id: 'expiring-contracts',
    name: 'Expiring contracts',
    description: 'Fixed-term contracts ending within the selected window (default: next 90 days).',
    category: 'hr',
    domain: 'employee',
    kind: 'live',
    requiredPermissions: ['employee:write'],
    filters: ['dateRange'],
    defaultRangeDays: 90,
    fetch: fetchExpiringContracts,
  },
  {
    id: 'probation-ending',
    name: 'Probation ending',
    description: 'Employees whose probation ends within the selected window (default: next 60 days).',
    category: 'hr',
    domain: 'employee',
    kind: 'live',
    requiredPermissions: ['employee:write'],
    filters: ['dateRange'],
    defaultRangeDays: 60,
    fetch: fetchProbationEnding,
  },
  {
    id: 'pending-leave',
    name: 'Pending leave approvals',
    description: 'Leave requests awaiting approval, with requester, dates and reason.',
    category: 'hr',
    domain: 'leave',
    kind: 'live',
    requiredPermissions: ['leave:approve'],
    fetch: fetchPendingLeave,
  },
  {
    id: 'team-leave-balances',
    name: 'Leave balances',
    description: 'Entitled / taken / pending / available leave days per team member.',
    category: 'hr',
    domain: 'leave',
    kind: 'live',
    requiredPermissions: ['leave:approve'],
    fetch: fetchTeamLeaveBalances,
  },

  // ---- Finance ----
  {
    id: 'payroll-readiness',
    name: 'Payroll readiness',
    description: 'Who can be paid this run: per-employee readiness status and blocking issues.',
    category: 'finance',
    domain: 'payroll_readiness',
    kind: 'live',
    requiredPermissions: ['payroll:read'],
    fetch: () => fetchPayrollReadinessRows(),
  },
  {
    id: 'blocked-from-payroll',
    name: 'Blocked from payroll',
    description: 'Employees blocked from the next payroll run and the issues blocking them.',
    category: 'finance',
    domain: 'payroll_readiness',
    kind: 'live',
    requiredPermissions: ['payroll:read'],
    fetch: () => fetchPayrollReadinessRows('blocked'),
  },
  {
    id: 'loans-deductions',
    name: 'Loans & advances',
    description: 'Active and pending staff loans/advances: principal, balance and monthly installment.',
    category: 'finance',
    domain: 'loans_advances',
    kind: 'live',
    requiredPermissions: ['payroll:read'],
    sensitive: true,
    fetch: fetchLoansDeductions,
  },
  statutoryPassthrough('export-paye', 'PAYE return (TRA)', 'TRA PAYE monthly return (CSV/XLSX) — generated from a locked payroll run.'),
  statutoryPassthrough('export-nssf', 'NSSF contributions', 'NSSF monthly contribution schedule (CSV/XLSX) — generated from a locked payroll run.'),
  statutoryPassthrough('export-sdl', 'SDL return', 'Skills Development Levy return (CSV/XLSX) — generated from a locked payroll run.'),
  statutoryPassthrough('export-wcf', 'WCF return', "Workers' Compensation Fund return (CSV/XLSX) — generated from a locked payroll run."),
  statutoryPassthrough('export-heslb', 'HESLB deductions', 'HESLB loan-deduction schedule (CSV/XLSX) — generated from a locked payroll run.'),
  statutoryPassthrough('export-bank-file', 'Bank payment file', 'Net-pay bank transfer file for the disbursement batch — generated from a locked payroll run.', 'payroll'),
  statutoryPassthrough('export-gl-journal', 'GL journal', 'Balanced general-ledger journal for accounting import — generated from a locked payroll run.', 'payroll'),
  statutoryPassthrough('export-payroll-register', 'Payroll register / net pay', 'Full payroll register with per-employee gross-to-net lines — generated from a locked payroll run.', 'payroll'),

  // ---- Attendance ----
  ...ATTENDANCE_STANDARD,
  {
    id: 'attendance-import-summary',
    name: 'Attendance import summary',
    description: 'Scanner import batches: rows, matches, exceptions and approval status.',
    category: 'attendance',
    domain: 'attendance',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    fetch: fetchAttendanceImportSummary,
  },
  {
    id: 'attendance-exceptions',
    name: 'Attendance exceptions',
    description: 'All attendance exceptions with severity and resolution status.',
    category: 'attendance',
    domain: 'attendance',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    filters: ['status'],
    statusOptions: ['open', 'resolved', 'ignored', 'all'],
    fetch: ({ status }) => fetchExceptions(status),
  },
  {
    id: 'missing-clock-out',
    name: 'Missing clock-out',
    description: 'Days where an employee clocked in but never clocked out.',
    category: 'attendance',
    domain: 'attendance',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    filters: ['status'],
    statusOptions: ['open', 'resolved', 'ignored', 'all'],
    fetch: ({ status }) => fetchExceptions(status, 'missing_clock_out'),
  },
  {
    id: 'on-leave-but-scanned',
    name: 'On leave but scanned',
    description: 'Employees on approved leave who still scanned at the device — a fraud signal.',
    category: 'attendance',
    domain: 'attendance',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    filters: ['status'],
    statusOptions: ['open', 'resolved', 'ignored', 'all'],
    fetch: ({ status }) => fetchExceptions(status, 'on_leave_scanned'),
  },
  {
    id: 'late-arrivals',
    name: 'Late arrivals',
    description: 'Clock-ins after the grace period, by day and employee.',
    category: 'attendance',
    domain: 'attendance',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    filters: ['status'],
    statusOptions: ['open', 'resolved', 'ignored', 'all'],
    fetch: ({ status }) => fetchExceptions(status, 'late_arrival'),
  },
  {
    id: 'potential-overtime',
    name: 'Potential overtime',
    description: 'Overtime detected from scans that still needs approval before payroll.',
    category: 'attendance',
    domain: 'overtime',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    fetch: makeOvertimeFetch('pending'),
  },
  {
    id: 'approved-overtime',
    name: 'Approved overtime',
    description: 'Overtime approved for payment, with approved minutes and multiplier.',
    category: 'attendance',
    domain: 'overtime',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    fetch: makeOvertimeFetch('approved'),
  },
  {
    id: 'rejected-overtime',
    name: 'Rejected overtime',
    description: 'Overtime requests that were rejected, with the recorded reason state.',
    category: 'attendance',
    domain: 'overtime',
    kind: 'live',
    requiredPermissions: ['attendance:read'],
    fetch: makeOvertimeFetch('rejected'),
  },

  // ---- Risk & Fraud ----
  {
    id: 'duplicate-identity',
    name: 'Duplicate identities',
    description: 'Duplicate TIN/NSSF/national-ID/bank values across employees — ghost-worker signals.',
    category: 'risk',
    domain: 'risk_fraud',
    kind: 'live',
    requiredPermissions: ['payroll:approve'],
    sensitive: true,
    fetch: fetchDuplicateIdentity,
  },

  // ---- Audit ----
  {
    id: 'sensitive-changes',
    name: 'Sensitive changes',
    description: 'Pending and decided sensitive-change requests (maker-checker).',
    category: 'audit',
    domain: 'audit',
    kind: 'passthrough',
    requiredPermissions: ['employee:change:approve'],
    route: 'admin/change-approvals',
  },
  {
    id: 'employee-change-history',
    name: 'Employee change history',
    description: 'Field-level change history per employee. Open an employee → History tab.',
    category: 'audit',
    domain: 'audit',
    kind: 'passthrough',
    requiredPermissions: ['employee:write'],
    route: 'employees',
  },
];

// ---- Registry API ---------------------------------------------------------------

export const CATEGORY_ORDER: ReportCategory[] = ['owner', 'hr', 'finance', 'attendance', 'risk', 'audit'];

export const CATEGORY_LABELS: Record<ReportCategory, string> = {
  owner: 'Owner',
  hr: 'HR',
  finance: 'Finance',
  attendance: 'Attendance',
  risk: 'Risk & Fraud',
  audit: 'Audit',
};

export function getTemplate(id: string): ReportTemplate | undefined {
  return REPORT_TEMPLATES.find((t) => t.id === id);
}

/** True when `roles` include any tenant role beyond plain `employee`. */
const isManagerPlus = (roles: string[]) => roles.some((r) => r !== 'employee');

/** Whether the given canonical roles can see a template (ANY-of + manager gate). */
export function canSeeTemplate(roles: string[], tpl: ReportTemplate): boolean {
  if (tpl.managerOnly && !isManagerPlus(roles)) return false;
  return tpl.requiredPermissions.some((p) => hasPermission(roles, p));
}

/** All templates visible to `roles`, in registry order. */
export function visibleTemplates(roles: string[]): ReportTemplate[] {
  return REPORT_TEMPLATES.filter((t) => canSeeTemplate(roles, t));
}

/** Templates listed under `category` (primary + alsoIn). */
export function templatesInCategory(templates: ReportTemplate[], category: ReportCategory): ReportTemplate[] {
  return templates.filter((t) => t.category === category || t.alsoIn?.includes(category));
}
